import { Box, Grid, Paper, Tabs, Tab, useTheme } from "@mui/material";
import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";

const tabStyle = {
  textTransform: "capitalize",
  fontWeight: "bold",
  borderRadius: "10px",
};

const FinanceNav = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [value, setValue] = useState("/finance");
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { data: pendingPrescriptions } = useFetchObjects(
    ["prescriptions", "unpaid-count"],
    "prescriptions/?payment_status=1&page=1&page_size=1",
    token,
    { refetchInterval: 10000 },
  );
  const { data: pendingMedicineOrders } = useFetchObjects(
    ["medicine-orders", "unpaid-count"],
    "medicine-orders/?payment_status=1&page=1&page_size=1",
    token,
    { refetchInterval: 10000 },
  );
  const hasPendingPrescriptionFees = (pendingPrescriptions?.count || 0) > 0;
  const hasPendingMedicineOrderFees = (pendingMedicineOrders?.count || 0) > 0;

  const handleChange = (event, newValue) => {
    setValue(newValue);
    navigate(newValue);
  };

  useEffect(() => {
    const path = location.pathname;
    const validPaths = [
      "/finance",
      "/finance/patients",
      "/finance/collect-prescription-fees",
      "/finance/collect-medicine-order-fees",
      "/finance/expenses-type",
      "/finance/expense-source",
      "/finance/expenses",
      "/finance/expense-payments",
      "/finance/payroll",
      "/finance/advance",
      "/finance/bonus",
      "/finance/approve",
      "/finance/approve-discount",
      "/finance/medicine-payment",
      "/finance/comprehensive-reports",
    ];

    if (path.startsWith("/finance/collect-prescription-fees")) {
      setValue("/finance/collect-prescription-fees");
    } else if (path.startsWith("/finance/collect-medicine-order-fees")) {
      setValue("/finance/collect-medicine-order-fees");
    } else if (path.startsWith("/finance/expense-payments")) {
      setValue("/finance/expense-payments");
    } else if (path.startsWith("/finance/expenses-type")) {
      setValue("/finance/expenses-type");
    } else if (path.startsWith("/finance/expenses")) {
      setValue("/finance/expenses");
    } else if (path.startsWith("/finance/medicine-payment")) {
      setValue("/finance/medicine-payment");
    } else if (path.startsWith("/finance/report")) {
      setValue("/finance/report");
    } else if (path.startsWith("/finance/comprehensive-reports")) {
      setValue("/finance/comprehensive-reports");
    } else if (validPaths.includes(path)) {
      setValue(path);
    } else {
      setValue("/finance");
    }
  }, [location.pathname]);

  return (
    <Grid container maxWidth="xl">
      <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
        <Paper sx={{ padding: "10px 0px", boxShadow: "0px 0px 0px" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              ml: "5px",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": { minWidth: "120px" },
            }}
          >
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance" ? theme.palette.primary.main : "white",
                color: value === "/finance" ? "white" : "black",
              }}
              label={t("finance.dashboard")}
              value="/finance"
              component={NavLink}
              to="/finance"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/patients"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/patients" ? "white" : "black",
              }}
              label={t("finance.patients")}
              value="/finance/patients"
              component={NavLink}
              to="/finance/patients"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor: hasPendingPrescriptionFees
                  ? "#8b0000"
                  : value === "/finance/collect-prescription-fees"
                    ? theme.palette.primary.main
                    : "white",
                color:
                  hasPendingPrescriptionFees ||
                  value === "/finance/collect-prescription-fees"
                    ? "white"
                    : "black",
              }}
              label={t("finance.prescriptionFees")}
              value="/finance/collect-prescription-fees"
              component={NavLink}
              to="/finance/collect-prescription-fees"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor: hasPendingMedicineOrderFees
                  ? "#8b0000"
                  : value === "/finance/collect-medicine-order-fees"
                    ? theme.palette.primary.main
                    : "white",
                color:
                  hasPendingMedicineOrderFees ||
                  value === "/finance/collect-medicine-order-fees"
                    ? "white"
                    : "black",
              }}
              label={t("finance.medicineFees")}
              value="/finance/collect-medicine-order-fees"
              component={NavLink}
              to="/finance/collect-medicine-order-fees"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/payroll"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/payroll" ? "white" : "black",
              }}
              label={t("finance.payroll")}
              value="/finance/payroll"
              component={NavLink}
              to="/finance/payroll"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/advance"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/advance" ? "white" : "black",
              }}
              label={t("finance.advance")}
              value="/finance/advance"
              component={NavLink}
              to="/finance/advance"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/bonus"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/bonus" ? "white" : "black",
              }}
              label={t("finance.bonus")}
              value="/finance/bonus"
              component={NavLink}
              to="/finance/bonus"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/expenses-type"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/expenses-type" ? "white" : "black",
              }}
              label={t("finance.expenseType")}
              value="/finance/expenses-type"
              component={NavLink}
              to="/finance/expenses-type"
            />
            {/* <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/expense-source"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/expense-source" ? "white" : "black",
              }}
              label={t("finance.expenseSource")}
              value="/finance/expense-source"
              component={NavLink}
              to="/finance/expense-source"
            /> */}
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/expenses"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/expenses" ? "white" : "black",
              }}
              label={t("finance.expenses")}
              value="/finance/expenses"
              component={NavLink}
              to="/finance/expenses"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/expense-payments"
                    ? theme.palette.primary.main
                    : "white",
                color:
                  value === "/finance/expense-payments" ? "white" : "black",
              }}
              label={t("finance.expensePayments")}
              value="/finance/expense-payments"
              component={NavLink}
              to="/finance/expense-payments"
            />

            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/approve"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/approve" ? "white" : "black",
              }}
              label={t("finance.approve")}
              value="/finance/approve"
              component={NavLink}
              to="/finance/approve"
            />

            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/medicine-payment"
                    ? theme.palette.primary.main
                    : "white",
                color:
                  value === "/finance/medicine-payment" ? "white" : "black",
              }}
              label={t("finance.medicinePayment")}
              value="/finance/medicine-payment"
              component={NavLink}
              to="/finance/medicine-payment"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/report"
                    ? theme.palette.primary.main
                    : "white",
                color: value === "/finance/report" ? "white" : "black",
              }}
              label={t("finance.report")}
              value="/finance/report"
              component={NavLink}
              to="/finance/report"
            />
            <Tab
              style={{
                ...tabStyle,
                backgroundColor:
                  value === "/finance/comprehensive-reports"
                    ? theme.palette.primary.main
                    : "white",
                color:
                  value === "/finance/comprehensive-reports"
                    ? "white"
                    : "black",
              }}
              label={t("finance.comprehensiveReports")}
              value="/finance/comprehensive-reports"
              component={NavLink}
              to="/finance/comprehensive-reports"
            />
          </Tabs>
        </Paper>
      </Box>
      <Outlet />
    </Grid>
  );
};

export default FinanceNav;
